import { useEffect, useMemo, useRef, useState } from "react";

import { ReactComponent as ReminderIcon } from "../../../assets/icons/notification.svg";

import styles from "./CommunicationDetailPanel.module.css";

const DAY_NAMES = ["L", "M", "M", "J", "V", "S", "D"];
const MONTH_NAMES = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];

const HOUR_OPTIONS = Array.from({ length: 24 }, (_, hour) => ({
  value: hour,
  label: `${String(hour).padStart(2, "0")}:00`,
}));

const pad = (n) => String(n).padStart(2, "0");

const toISODate = (date) =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

const parseISODate = (value) => {
  if (!value) return null;
  const [year, month, day] = value.split("-").map(Number);
  if (!year || !month || !day) return null;
  const parsed = new Date(year, month - 1, day);
  // new Date normaliza overflows (2026-02-31 -> 3 de marzo); los rechazamos.
  const matchesInput =
    parsed.getFullYear() === year &&
    parsed.getMonth() === month - 1 &&
    parsed.getDate() === day;
  return matchesInput ? parsed : null;
};

const startOfToday = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
};

// Hora actual redondeada hacia abajo (15:59 -> 15).
const currentHour = () => new Date().getHours();

const isSameDay = (a, b) =>
  a &&
  b &&
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

const getFirstDayOfMonth = (year, month) => {
  const day = new Date(year, month, 1).getDay();
  return day === 0 ? 6 : day - 1;
};

const getDaysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();

const ReminderPicker = ({
  date = "",
  hour = "",
  onChange,
  disabled = false,
  due = false,
  saving = false,
}) => {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  const selectedDate = useMemo(() => parseISODate(date), [date]);

  const [viewYear, setViewYear] = useState(
    () => (selectedDate || new Date()).getFullYear(),
  );
  const [viewMonth, setViewMonth] = useState(
    () => (selectedDate || new Date()).getMonth(),
  );
  const [draftDate, setDraftDate] = useState(selectedDate);
  const [draftHour, setDraftHour] = useState(
    hour === "" ? currentHour() : Number(hour),
  );

  useEffect(() => {
    if (!open) return undefined;
    const handleOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, [open]);

  const handleOpen = () => {
    // Una fecha ya pasada no se precarga: obligaría a re-aplicarla sin poder
    // re-seleccionarla (los días pasados están deshabilitados en la grilla).
    const initialDraft =
      selectedDate && selectedDate >= startOfToday() ? selectedDate : null;
    const base = initialDraft || new Date();
    setViewYear(base.getFullYear());
    setViewMonth(base.getMonth());
    setDraftDate(initialDraft);
    setDraftHour(hour === "" ? currentHour() : Number(hour));
    setOpen(true);
  };

  const prevMonth = () => {
    if (viewMonth === 0) {
      setViewMonth(11);
      setViewYear((y) => y - 1);
    } else {
      setViewMonth((m) => m - 1);
    }
  };

  const nextMonth = () => {
    if (viewMonth === 11) {
      setViewMonth(0);
      setViewYear((y) => y + 1);
    } else {
      setViewMonth((m) => m + 1);
    }
  };

  const handleApply = () => {
    onChange?.({ date: toISODate(draftDate), hour: Number(draftHour) });
    setOpen(false);
  };

  const handleClear = () => {
    setDraftDate(null);
    setDraftHour("");
    onChange?.({ date: "", hour: "" });
    setOpen(false);
  };

  const cells = [];
  const firstDay = getFirstDayOfMonth(viewYear, viewMonth);
  const daysInMonth = getDaysInMonth(viewYear, viewMonth);
  for (let i = 0; i < firstDay; i += 1) cells.push(null);
  for (let d = 1; d <= daysInMonth; d += 1) {
    cells.push(new Date(viewYear, viewMonth, d));
  }

  const today = startOfToday();
  const hasDateAndHour = Boolean(selectedDate) && hour !== "";
  const isDue = due && hasDateAndHour;
  const hasReminder = hasDateAndHour && (isDue || selectedDate >= today);
  const summary = hasDateAndHour
    ? `${pad(selectedDate.getDate())}/${pad(selectedDate.getMonth() + 1)} · ${pad(Number(hour))}:00`
    : "";
  const triggerLabel = isDue
    ? `Vencido ${summary}`
    : hasReminder
      ? summary
      : "Recordatorio";
  const triggerClassName = [
    styles.aiButton,
    isDue ? styles.reminderButtonDue : "",
    !isDue && hasReminder ? styles.reminderButtonActive : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={styles.reminderWrapper} ref={wrapperRef}>
      <button
        type="button"
        className={triggerClassName}
        onClick={open ? () => setOpen(false) : handleOpen}
        disabled={disabled || saving}
        aria-busy={saving}
        title={
          isDue
            ? "Recordatorio vencido: reagendalo o quitalo"
            : "Configurar recordatorio de seguimiento"
        }
        aria-label="Configurar recordatorio de seguimiento"
      >
        <ReminderIcon className={styles.aiButtonIcon} />
        <span>{triggerLabel}</span>
      </button>

      {open && (
        <div className={styles.reminderPopover}>
          <div className={styles.reminderCalendarNav}>
            <button
              type="button"
              className={styles.reminderNavBtn}
              onClick={prevMonth}
              aria-label="Mes anterior"
            >
              ‹
            </button>
            <span className={styles.reminderMonthTitle}>
              {MONTH_NAMES[viewMonth]} {viewYear}
            </span>
            <button
              type="button"
              className={styles.reminderNavBtn}
              onClick={nextMonth}
              aria-label="Mes siguiente"
            >
              ›
            </button>
          </div>

          <div className={styles.reminderGrid}>
            {DAY_NAMES.map((name, i) => (
              <div key={i} className={styles.reminderDayName}>
                {name}
              </div>
            ))}
            {cells.map((cellDate, i) => {
              if (!cellDate) return <div key={`empty-${i}`} />;
              const isPast = cellDate < today;
              const isSelected = isSameDay(cellDate, draftDate);
              const isToday = isSameDay(cellDate, today);
              return (
                <button
                  key={toISODate(cellDate)}
                  type="button"
                  className={[
                    styles.reminderDay,
                    isSelected ? styles.reminderDaySelected : "",
                    isToday && !isSelected ? styles.reminderDayToday : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  onClick={() => setDraftDate(cellDate)}
                  disabled={isPast}
                >
                  {cellDate.getDate()}
                </button>
              );
            })}
          </div>

          <div className={styles.reminderHourRow}>
            <span className={styles.reminderHourLabel}>Hora</span>
            <select
              className={styles.reminderHourSelect}
              value={draftHour}
              onChange={(event) =>
                setDraftHour(
                  event.target.value === "" ? "" : Number(event.target.value),
                )
              }
              aria-label="Hora del recordatorio"
            >
              <option value="">--:--</option>
              {HOUR_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <p className={styles.reminderHint}>
            Se interpreta en tu zona horaria.
          </p>

          <div className={styles.reminderFooter}>
            <button
              type="button"
              className={styles.reminderClearBtn}
              onClick={handleClear}
            >
              Quitar
            </button>
            <button
              type="button"
              className={styles.reminderApplyBtn}
              onClick={handleApply}
              disabled={!draftDate || draftHour === ""}
            >
              Aplicar
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReminderPicker;
